import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { RelatorioDemanda } from '@/features/relatorios/tipos';
import { formatarInteiro, formatarPercentual } from '@/features/relatorios/formato';
import { GraficoBarras } from '@/components/graficos/barras';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { CartaoGrafico } from './cartoes';
import { EsqueletoBloco } from './esqueleto-bloco';
import { ErroBloco } from './erro-bloco';

export function BlocoDemanda({ consulta }: { consulta: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['relatorio', 'demanda', consulta],
    queryFn: () => api.get<RelatorioDemanda>(`/relatorios/demanda?${consulta}`),
  });

  if (isPending) return <EsqueletoBloco />;
  if (error) return <ErroBloco erro={error} />;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <CartaoGrafico
          titulo="Especialidades mais solicitadas"
          descricao="Vem da especialidade escolhida no formulário — contagem exata."
        >
          <GraficoBarras
            itens={data.porEspecialidade.map((linha) => ({
              chave: linha.especialidadeId,
              rotulo: linha.nome,
              valor: linha.total,
            }))}
          />
        </CartaoGrafico>

        <CartaoGrafico
          titulo="Procedimentos mais solicitados"
          descricao="O campo é digitado à mão pela UBS: grafias diferentes do mesmo exame contam separado."
        >
          <GraficoBarras
            vazio="Nenhum procedimento informado no período."
            itens={data.porProcedimento.map((linha) => ({
              chave: linha.procedimento,
              rotulo: linha.procedimento,
              valor: linha.total,
              cor: 'bg-grafico-2',
            }))}
          />
        </CartaoGrafico>
      </div>

      <CartaoGrafico
        titulo="Especialidades que mais geram atrito"
        descricao="Solicitações que passaram por pendência ou devolução. Ordenadas pelo número de casos; a taxa mostra quanto isso pesa dentro da especialidade."
      >
        <Tabela>
          <TabelaCabecalho>
            <TabelaLinha>
              <TabelaTitulo>Especialidade</TabelaTitulo>
              <TabelaTitulo className="text-right">Solicitações</TabelaTitulo>
              <TabelaTitulo className="text-right">Com problema</TabelaTitulo>
              <TabelaTitulo className="text-right">Taxa</TabelaTitulo>
            </TabelaLinha>
          </TabelaCabecalho>
          <TabelaCorpo>
            {data.especialidadesComAtrito.length === 0 && (
              <TabelaVazia colunas={4}>
                Nenhuma pendência ou devolução registrada no período.
              </TabelaVazia>
            )}

            {data.especialidadesComAtrito.map((linha) => (
              <TabelaLinha key={linha.especialidadeId}>
                <TabelaCelula rotulo="Especialidade" className="font-medium">
                  {linha.nome}
                </TabelaCelula>
                <TabelaCelula rotulo="Solicitações" className="text-right tabular-nums">
                  {formatarInteiro(linha.total)}
                </TabelaCelula>
                <TabelaCelula rotulo="Com problema" className="text-right tabular-nums">
                  {formatarInteiro(linha.problemas)}
                </TabelaCelula>
                <TabelaCelula rotulo="Taxa" className="text-right tabular-nums">
                  {formatarPercentual(linha.taxaProblema)}
                </TabelaCelula>
              </TabelaLinha>
            ))}
          </TabelaCorpo>
        </Tabela>
      </CartaoGrafico>

      <CartaoGrafico
        titulo="O que cada UBS mais pede"
        descricao="Ranking de procedimentos dentro de cada unidade."
      >
        {data.procedimentoPorUbs.length === 0 ? (
          <p className="text-silenciado-texto py-6 text-center text-sm">
            Nenhum procedimento informado no período.
          </p>
        ) : (
          <ul className="divide-borda grid divide-y lg:grid-cols-2 lg:gap-x-8 lg:divide-y-0">
            {data.procedimentoPorUbs.map((unidade) => (
              <li key={unidade.ubsId} className="py-4 first:pt-0 lg:py-3">
                <h4 className="mb-2 text-sm font-semibold">{unidade.nome}</h4>
                <GraficoBarras
                  itens={unidade.itens.map((item) => ({
                    chave: `${unidade.ubsId}-${item.procedimento}`,
                    rotulo: item.procedimento,
                    valor: item.total,
                    cor: 'bg-grafico-2',
                  }))}
                />
              </li>
            ))}
          </ul>
        )}
      </CartaoGrafico>
    </div>
  );
}
